"use client";

import React, { useState, useEffect } from 'react';
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { Loader2 } from 'lucide-react';
import { Button } from '@/app/_module/components/ui/button';
import { useToast } from '@/app/_module/hooks/use-toast';
import { cn } from '@/app/_module/lib/utils';
import { A2P_PRICING } from '@/app/_module/types/a2p.types';
import { a2pPaymentService } from '@/app/_module/services/a2pPaymentService';
import { useBranding } from '@/app/_module/store/contexts/brandingContext';
import { Panel, focusRing } from './overview/primitives';
import { StepIntro } from './overview/wizard-ui';

interface PaymentStepProps {
  onPaymentComplete?: () => void;
  onSkip?: () => void;
}

interface PaymentCheckResult {
  paymentRequired: boolean;
  hasRejectedVerification: boolean;
  coveredByPlatform: boolean;
  message: string;
}

const money = (n: number) => `$${n.toFixed(2)}`;

export default function PaymentStep({ onPaymentComplete, onSkip }: PaymentStepProps) {
  const { toast } = useToast();
  const { branding } = useBranding();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentCheck, setPaymentCheck] = useState<PaymentCheckResult | null>(null);

  // Check payment requirement from backend (based on verification history, not subscription tier)
  useEffect(() => {
    const checkPaymentRequirement = async () => {
      try {
        setIsLoading(true);
        const result = await a2pPaymentService.checkPaymentRequired();
        setPaymentCheck(result);
      } catch (error) {
        // Default to covered by platform if check fails
        setPaymentCheck({
          paymentRequired: false,
          hasRejectedVerification: false,
          coveredByPlatform: true,
          message: 'Platform covers A2P verification fees'
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentRequirement();
  }, []);

  // Payment is only required if user has a previous rejected verification
  const paymentRequired = paymentCheck?.paymentRequired ?? false;

  const handleStripePayment = async () => {
    try {
      setIsProcessing(true);

      // Create Stripe checkout session via backend API
      const checkoutSession = await a2pPaymentService.createCheckoutSession();

      // Redirect user to Stripe checkout page
      if (checkoutSession.sessionUrl) {
        if (!allowCheckout("payment")) return;
        window.location.href = checkoutSession.sessionUrl;
      } else {
        throw new Error('No checkout session URL returned from server');
      }

    } catch (error: any) {

      toast({
        title: "Couldn't open checkout",
        description: error.message || 'Try again.',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
    // Note: setIsProcessing(false) is not called on success because we're redirecting away
  };

  const handleSkip = () => {
    // Skip is allowed when payment is not required (platform covers it)
    if (!paymentRequired) {
      onSkip?.();
    }
  };

  // Show loading state while checking payment requirement
  if (isLoading) {
    return (
      <div role="status" className="flex min-h-[240px] items-center justify-center gap-3 text-sm text-neutral-500">
        <Loader2 aria-hidden className="size-4 animate-spin" />
        Checking whether a payment is needed…
      </div>
    );
  }

  // If payment is NOT required (first attempt - platform covers), show skip message
  if (!paymentRequired) {
    return (
      <div className="space-y-8">
        <StepIntro title="No payment needed">
          <p>{branding.brandName} covers the carrier fees for your first registration.</p>
        </StepIntro>
        <Button size="lg" onClick={handleSkip} className={cn('px-6', focusRing)}>
          Submit for review
        </Button>
      </div>
    );
  }

  const { brandRegistration, campaignVetting } = A2P_PRICING.twilioBreakdown;
  const processing = A2P_PRICING.oneTimeFee - brandRegistration - campaignVetting;

  // Show payment form when payment IS required (after previous rejection)
  return (
    <div className="space-y-8">
      <StepIntro title="Pay the resubmission fee">
        <p>
          Your last registration was rejected, so a new one has a one-time fee. Fix whatever caused the rejection
          before you resubmit.
        </p>
      </StepIntro>

      <Panel>
        <dl className="divide-y divide-neutral-100 text-sm">
          <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
            <dt className="text-neutral-600">Brand registration</dt>
            <dd className="tabular-nums text-neutral-900">{money(brandRegistration)}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
            <dt className="text-neutral-600">Campaign review</dt>
            <dd className="tabular-nums text-neutral-900">{money(campaignVetting)}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
            <dt className="text-neutral-600">Processing and support</dt>
            <dd className="tabular-nums text-neutral-900">{money(processing)}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 bg-neutral-50/60 px-4 py-3.5 sm:px-5">
            <dt className="font-medium text-neutral-900">Total, one time</dt>
            <dd className="font-semibold tabular-nums text-neutral-900">
              {money(A2P_PRICING.oneTimeFee)} {A2P_PRICING.currency}
            </dd>
          </div>
        </dl>
      </Panel>

      <div className="space-y-2">
        <Button size="lg" onClick={handleStripePayment} disabled={isProcessing} className={cn('px-6', focusRing)}>
          {isProcessing && <Loader2 className="animate-spin" />}
          {isProcessing ? 'Opening checkout…' : 'Continue to checkout'}
        </Button>
        <p className="text-xs text-neutral-500">
          You&apos;ll pay on Stripe&apos;s checkout page. Your card details never reach our servers.
        </p>
      </div>
    </div>
  );
}
